import { useEffect, useRef, useState } from "react";
import NoIndex from "../NoIndex";
import useContactForm from "../../services/LandingPage/useContactForm";
import { Clock, Mail, Phone, Video } from "lucide-react";

export default function ContactDemo() {
  const [visible, setVisible] = useState(false);
  const { submitEnquiryDetails, loading } = useContactForm();
  const formRef = useRef(null);

  const [formData, setFormData] = useState({
    fullName: "",
    phoneNumber: "",
    email: "",
    enquiryType: "",
    description: "",
  });

  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [apiError, setApiError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phoneNumber") {
      const onlyDigits = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: onlyDigits }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setApiError("");
    setSuccessMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setApiError("");

    const form = new FormData(e.target);
    const latestFormData = {
      fullName: (form.get("fullName") || "").toString().trim(),
      phoneNumber: (form.get("phoneNumber") || "")
        .toString()
        .replace(/\D/g, "")
        .slice(0, 10),
      email: (form.get("email") || "").toString().trim(),
      enquiryType: (form.get("enquiryType") || "").toString().trim(),
      description: (form.get("description") || "").toString().trim(),
    };

    setFormData(latestFormData);

    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileRegex = /^[0-9]{10}$/;

    if (!latestFormData.fullName) newErrors.fullName = "Full name is required";
    if (!latestFormData.phoneNumber) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!mobileRegex.test(latestFormData.phoneNumber)) {
      newErrors.phoneNumber = "Phone number must be exactly 10 digits";
    }
    if (!latestFormData.email) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(latestFormData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!latestFormData.enquiryType)
      newErrors.enquiryType = "Please select inquiry type";
    if (!latestFormData.description)
      newErrors.description = "Description is required";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const result = await submitEnquiryDetails(latestFormData);
    if (result.success) {
      setSuccessMessage(result.message || "Inquiry submitted successfully");
      setFormData({
        fullName: "",
        phoneNumber: "",
        email: "",
        enquiryType: "",
        description: "",
      });
      setErrors({});
      e.target.reset();
    } else {
      setApiError(result.message || "Something went wrong");
    }
  };


  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const contactItems = [
    {
      icon: <Mail size={14} color="#185FA5" />,
      bg: "#EFF6FF",
      label: "Email",
      content: (
        <a
          href="mailto:support@dhroom.com"
          style={{
            fontSize: 11,
            fontWeight: 500,
            color: "#374151",
            wordBreak: "break-all",
            lineHeight: 1.3,
            textDecoration: "none",
            display: "block",
          }}
        >
          support@dhroom.com
        </a>
      ),
    },
    {
      icon: <Phone size={14} color="#0F6E56" />,
      bg: "#ECFDF5",
      label: "Phone",
      content: (
        <a
          href="tel:+9958853167"
          style={{
            fontSize: 11,
            fontWeight: 500,
            color: "#374151",
            textDecoration: "none",
            display: "block",
          }}
        >
          +91 9958853167
        </a>
      ),
    },
    {
      icon: <Clock size={14} color="#854F0B" />,
      bg: "#FFFBEB",
      label: "Hours",
      content: (
        <p
          style={{
            margin: 0,
            fontSize: 11,
            fontWeight: 500,
            color: "#374151",
            lineHeight: 1.4,
          }}
        >
          Mon – Sat
          <br />9 AM – 6 PM
        </p>
      ),
    },
    {
      icon: <Video size={14} color="#534AB7" />,
      bg: "#EEF2FF",
      label: "Demo",
      content: (
        <button
          style={{
            border: "none",
            background: "transparent",
            padding: 0,
            fontSize: 11,
            fontWeight: 500,
            color: "#534AB7",
            cursor: "pointer",
            lineHeight: 1.4,
            display: "block",
          }}
        >
          Book free
          <br />
          30-min call ↗
        </button>
      ),
    },
  ];

  return (
    <>
      <NoIndex />

      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-16">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:wght@300;400;500&display=swap');

          .font-display { font-family: 'Instrument Serif', serif; }
          .font-body   { font-family: 'DM Sans', sans-serif; }

          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50%       { transform: translateY(-10px); }
          }
          @keyframes ping-slow {
            0%   { transform: scale(1);   opacity: 0.5; }
            100% { transform: scale(1.8); opacity: 0; }
          }
          @keyframes ping-slower {
            0%   { transform: scale(1);   opacity: 0.3; }
            100% { transform: scale(2.2); opacity: 0; }
          }

          .anim-float  { animation: float       4s   ease-in-out infinite; }
          .anim-ping-1 { animation: ping-slow   2.4s ease-out   infinite; }
          .anim-ping-2 { animation: ping-slower 2.4s ease-out   infinite 0.8s; }

          .chip:hover { border-color: rgba(0,0,0,0.2); }

          .form-input {
            height: 44px;
            width: 100%;
            border-radius: 10px;
            border: 1px solid #d1d5db;
            background: #f7f7f7;
            padding: 0 14px;
            font-size: 13px;
            color: #374151;
            outline: none;
            transition: border-color 0.15s;
            box-sizing: border-box;
          }
          .form-input:focus { border-color: #22c55e; background: #fff; }

          .form-textarea {
            width: 100%;
            border-radius: 10px;
            border: 1px solid #d1d5db;
            background: #f7f7f7;
            padding: 10px 14px;
            font-size: 13px;
            color: #374151;
            outline: none;
            resize: none;
            transition: border-color 0.15s;
            font-family: 'DM Sans', sans-serif;
            box-sizing: border-box;
          }
          .form-textarea:focus { border-color: #22c55e; background: #fff; }

          .form-label {
            display: block;
            font-size: 12px;
            font-weight: 500;
            color: #374151;
            margin-bottom: 4px;
          }

          /* ── Divider: vertical on ≥md, horizontal on <md ── */
          .panel-divider-v {
            display: none;
          }
          .panel-divider-h {
            display: block;
            height: 1px;
            background: #e2e8f0;
            flex-shrink: 0;
          }
          @media (min-width: 768px) {
            .panel-divider-v {
              display: block;
              width: 1px;
              background: #e2e8f0;
              flex-shrink: 0;
              align-self: stretch;
            }
            .panel-divider-h {
              display: none;
            }
          }

          /* ── 3-col layout ── */
          .three-col-card {
            display: flex;
            flex-direction: column;
          }
          @media (min-width: 768px) {
            .three-col-card {
              flex-direction: row;
            }
          }

          /* ── Contact grid ── */
          .contact-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0;
          }

          .contact-cell {
            padding: 14px 10px;
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
          }
          .contact-cell:nth-child(1),
          .contact-cell:nth-child(2) {
            border-bottom: 1px solid #f0f0f0;
          }
          .contact-cell:nth-child(odd) {
            border-right: 1px solid #f0f0f0;
          }
        `}</style>

        {/* ── Outer card: all 3 panels in one row ── */}
        <div className="font-body three-col-card w-full max-w-7xl rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white">
          {/* ══ PANEL 1: Hero / Left ══ */}
          <div
            className="flex flex-col items-center justify-center text-center gap-6 px-10 py-14 bg-white"
            style={{ flex: "1 1 0" }}
          >
            {/* Pulse animation */}
            <div className="relative w-20 h-20 flex items-center justify-center mx-auto">
              <div className="anim-ping-1 absolute inset-0 rounded-full bg-blue-200" />
              <div className="anim-ping-2 absolute inset-0 rounded-full bg-blue-100" />
              <div className="anim-float relative z-10 w-20 h-20 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
                <svg
                  width="34"
                  height="34"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#2563eb"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 10l4.553-2.069A1 1 0 0121 8.871V15.13a1 1 0 01-1.447.9L15 14" />
                  <rect x="2" y="6" width="14" height="12" rx="2" />
                </svg>
              </div>
            </div>

            <h1 className="font-display text-4xl text-slate-900 leading-tight">
              See it live.
              <br />
              <span className="italic text-blue-600">Book a free demo.</span>
            </h1>

            <div className="contact-grid">
              {contactItems.map((item, i) => (
                <div key={i} className="contact-cell">
                  <div
                    className="rounded flex items-center justify-center mb-2"
                    style={{
                      background: item.bg,
                      padding: 8,
                      width: 30,
                      height: 30,
                    }}
                  >
                    {item.icon}
                  </div>
                  <small
                    className="block uppercase mb-1 text-slate-500"
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                    }}
                  >
                    {item.label}
                  </small>
                  {item.content}
                </div>
              ))}
            </div>
          </div>

          {/* ── Divider 1 ── */}
          <div className="panel-divider-v" />
          <div className="panel-divider-h" />

          {/* ══ PANEL 2: Form ══ */}
          <div
            className="flex flex-col justify-center px-10 py-10 bg-[#fdfffd]"
            style={{ flex: "1 1 0" }}
          >
            <h3 className="text-center text-xl font-semibold text-green-600">
              Send an inquiry
            </h3>
            <p className="mt-1 mb-5 text-center text-xs text-slate-500">
              Fill in your details and we'll get back to you.
            </p>

            <form ref={formRef} onSubmit={handleSubmit} className="space-y-3">
              {/* Name + Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="form-label" htmlFor="fullName">
                    Full Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="fullName"
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    autoComplete="name"
                    className="form-input"
                  />
                  {errors.fullName && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.fullName}
                    </p>
                  )}
                </div>
                <div>
                  <label className="form-label" htmlFor="phoneNumber">
                    Phone Number <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="phoneNumber"
                    type="text"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder="10-digit number"
                    maxLength={10}
                    autoComplete="tel"
                    className="form-input"
                  />
                  {errors.phoneNumber && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.phoneNumber}
                    </p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="form-label" htmlFor="email">
                  Email Address <span className="text-red-400">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  autoComplete="email"
                  className="form-input"
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                )}
              </div>

              {/* Inquiry Type */}
              <div>
                <label className="form-label" htmlFor="enquiryType">
                  Inquiry Type <span className="text-red-400">*</span>
                </label>
                <select
                  id="enquiryType"
                  name="enquiryType"
                  value={formData.enquiryType}
                  onChange={handleChange}
                  className="form-input"
                  style={{ cursor: "pointer" }}
                >
                  <option value="" disabled>
                    Select inquiry type
                  </option>
                  <option value="Demo Request">Demo Request</option>
                  <option value="Pricing">Pricing</option>
                  <option value="Support">Support</option>
                  <option value="Partnership">Partnership</option>
                  <option value="enquiry">Enquiry</option>
                  <option value="feature">Feature</option>
                </select>
                {errors.enquiryType && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.enquiryType}
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="form-label" htmlFor="description">
                  Description <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="description"
                  rows="3"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Tell us about your requirement..."
                  autoComplete="off"
                  className="form-textarea"
                />
                {errors.description && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.description}
                  </p>
                )}
              </div>

              {apiError && (
                <p className="text-center text-sm font-medium text-red-500">
                  {apiError}
                </p>
              )}
              {successMessage && (
                <p className="text-center text-sm font-medium text-green-600">
                  {successMessage}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="h-11 w-full rounded-xl bg-green-500 text-sm font-semibold text-white transition hover:bg-green-600 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {loading ? "Submitting..." : "Send Inquiry"}
              </button>
            </form>
          </div>

          {/* ── Divider 2 ── */}
          <div className="panel-divider-v" />
          <div className="panel-divider-h" />
        </div>
      </div>
    </>
  );
}
